import { registerPlugin, WebPlugin } from '@capacitor/core'

/**
 * `SimCall`: the Android plugin in `android/app/src/main/java/com/ysm/genericworkflow/simcall/SimCallPlugin.kt`.
 * Places calls on the agent's SIM and reads the call log after the fact. Not available in browsers.
 */

export type SimPermissionState = 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale'

export interface SimPermissionStatus {
  callPhone: SimPermissionState
  callLog: SimPermissionState
  phoneState: SimPermissionState
}

export interface SimAccount {
  id: string
  label: string
  number?: string
}

export type CallLogType = 'INCOMING' | 'OUTGOING' | 'MISSED' | 'REJECTED' | 'OTHER'

export interface CallLogRow {
  /** CallLog.Calls._ID, unique per device. */
  id: string
  number: string
  type: CallLogType
  /** Epoch millis when the call started. */
  date: number
  durationSec: number
  phoneAccountId?: string
}

export interface SimCallPlugin {
  checkPermissions(): Promise<SimPermissionStatus>
  requestPermissions(): Promise<SimPermissionStatus>
  listSimAccounts(): Promise<{ accounts: SimAccount[] }>
  placeCall(options: { number: string; phoneAccountId?: string }): Promise<void>
  /** Newest last, at most 500 rows, optionally only one SIM's calls. */
  queryCallLog(options: { sinceMillis: number; phoneAccountId?: string }): Promise<{ entries: CallLogRow[] }>
  /** Whether this build includes Firebase config; push registration crashes the app without it. */
  pushConfigured(): Promise<{ configured: boolean }>
}

class SimCallWeb extends WebPlugin implements SimCallPlugin {
  private notAvailable(): never {
    throw this.unavailable('SIM calling is only available in the Android app')
  }
  async checkPermissions(): Promise<SimPermissionStatus> {
    return this.notAvailable()
  }
  async requestPermissions(): Promise<SimPermissionStatus> {
    return this.notAvailable()
  }
  async listSimAccounts(): Promise<{ accounts: SimAccount[] }> {
    return this.notAvailable()
  }
  async placeCall(): Promise<void> {
    return this.notAvailable()
  }
  async queryCallLog(): Promise<{ entries: CallLogRow[] }> {
    return this.notAvailable()
  }
  async pushConfigured(): Promise<{ configured: boolean }> {
    return { configured: false }
  }
}

export const SimCall = registerPlugin<SimCallPlugin>('SimCall', {
  web: () => new SimCallWeb(),
})
